import React, { useState, useRef, useEffect } from 'react';
import _merge from 'lodash/merge';
import Big from 'big.js';
import { useCombinedRefs, getContext, KEY_CODES } from 'common';
import IconButton, { IconButtonProps } from 'components/IconButton';
import TextInput, { TextInputProps } from 'components/TextInput';
import AddIcon from '@nokia-csf-uxr/ccfk-assets/AddIcon';
import RemoveIcon from '@nokia-csf-uxr/ccfk-assets/RemoveIcon';

import styled from './styles';

const ACTION = {
  increment: 'increment',
  decrement: 'decrement',
  enter: 'enter'
};

const ACTIONS = {
  decrement: 'decrement',
  increment: 'increment'
};

interface SpinnerProps extends Omit<React.ComponentPropsWithRef<'div'>, 'onChange'> {
  /** Callback handler which is executed when the spinner value is changed. Returns object of shape ({event, value}):
   *   - event: event
   *   - value: Number(value), where value is the current spinner value
   */
  onChange?: (arg: { event: React.MouseEvent | React.KeyboardEvent, value: number | string }) => void;
  /** The value the spinner should be set to. */
  value?: number | string;
  /** Type of outlook of Spinner. outlined or underlined */
  variant?: 'outlined' | 'underlined';
  /** The maximum value the spinner can be set to. */
  max?: number,
  /** The minimum value the spinner can be set to. */
  min?: number,
  /** The increment the value will increase/decrease when the +/- buttons are pressed. */
  step?: number,
  /**
    * Array of additional values which can be included, either stand-alone
    * or in conjunction with spinner range.
    */
  values?: number[];
  /** If there is an error in the input. */
  error?: boolean;
  /** Placeholder text for the spinner input field. Note: Giving this prop a value allows spinner to accept empty strings without error */
  placeholder?: string,
  /** If `true`, the component will be disabled. Note: The native input element's `disabled` attribute wills be set using this prop. Note: If the `disabled` prop is not set, it will be internally taken from the immediate parent `Label` component. */
  disabled?: boolean;
  /** If `true`, the field cannot be edited by the user. Note: The native input element's `readonly` attribute will be set using this prop.  Note: If the `readOnly` prop is not set, it will be internally taken from the immediate parent `Label` component. */
  readOnly?: boolean;
  /** Props for TextInput. Please check TextInput component for details. */
  inputProps?: Partial<TextInputProps>,
  /** Props just for decrement IconButton */
  decrementButtonProps?: Partial<IconButtonProps>;
  /** Props just for increment IconButton */
  incrementButtonProps?: Partial<IconButtonProps>;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** @ignore */
  onFocus?: React.FocusEventHandler;
}

/**
 *  Spinner component.
 * Any other props than the explicitly defined props will be provided to the root element.
 * To pass props to the internal `TextInput` component, please use `inputProps`.
 * To pass props to the internal `IconButton` component, please use `incrementButtonProps` or 'decrementButtonProps'.
 * To get the ref of the outermost element use `ref` prop. [Read React useRef hook documentation](https://reactjs.org/docs/hooks-reference).
 */
const Spinner = React.forwardRef<HTMLElement, SpinnerProps>((props: SpinnerProps, ref) =>
  getContext(({ RTL, theme }) => {
    const {
      value,
      max,
      min,
      values,
      step,
      onChange,
      onFocus,
      variant,
      error,
      readOnly,
      disabled,
      theme: propTheme,
      inputProps,
      incrementButtonProps,
      decrementButtonProps,
      placeholder,
      ...otherProps
    } = props;

    const {
      ref: textInputRef,
      onBlur,
      onKeyDown,
      inputProps: otherInputProps,
      ...otherTextInputProps
    } = inputProps;

    const { ref: incBtnPropRef, ...otherIncBtnProps } = incrementButtonProps;
    const { ref: decBtnPropRef, ...otherDecBtnProps } = decrementButtonProps;

    const resolvedTheme: any = _merge({}, theme.TEXT_INPUT.OUTLINED, propTheme);

    const [inputFocused, setInputFocused] = useState(false);
    const [currentValue, setCurrentValue] = useState(value);
    const inputRef = useRef();
    const incBtnRef = useRef();
    const decBtnRef = useRef();
    const combinedInputRef = textInputRef ? useCombinedRefs(textInputRef, inputRef) : inputRef;
    const combinedDecBtnRef = decBtnRef ? useCombinedRefs(decBtnPropRef, decBtnRef) : decBtnRef;
    const combinedIncBtnRef = incBtnRef ? useCombinedRefs(incBtnPropRef, incBtnRef) : incBtnRef;
    const isFirefox = useRef(navigator.userAgent.indexOf('Firefox') !== -1);
    const EMPTY_STRING = '';

    const validateValue = (newValue) => {
      let validatedValue = newValue;
      if (validatedValue === EMPTY_STRING && placeholder) {
        validatedValue = undefined;
        return EMPTY_STRING;
      } else if (Number.isNaN(validatedValue * 1)) {
        validatedValue = value;
      } else if (max != null && validatedValue > max) {
        validatedValue = max;
      } else if (min != null && validatedValue < min) {
        validatedValue = min;
      }
      return validatedValue * 1;
    };

    useEffect(() => {
      if (value !== currentValue) {
        const validatedValue = validateValue(value);
        setCurrentValue(validatedValue);
      }
    }, [value]);

    const calculateValue = (val, action) => {
      let res: number;
      if (val === EMPTY_STRING && placeholder) {
        if (action === ACTION.increment) {
          if (values) {
            res = Number(validateValue(values[0]));
            return res;
          }
          return 1;
        }
        return validateValue(val);
      }
      if (values && values.length > 0) {
        let index = values.indexOf(Number(validateValue(val)));
        if (index === -1) {
          index = values.indexOf(Number(value));
        } else {
          if (action === ACTION.increment && index < values.length - 1) {
            index += 1;
          }
          if (action === ACTION.decrement && index > 0) {
            index -= 1;
          }
        }
        res = values[index];
      } else {
        let newValue = new Big(val);
        const stepDecimal = new Big(step);
        if (action === ACTION.increment) {
          newValue = newValue.plus(stepDecimal);
        } else if (action === ACTION.decrement) {
          newValue = newValue.minus(stepDecimal);
        }
        res = Number(validateValue(newValue));
      }
      return res;
    };

    const handleChange = (event, action) => {
      const validatedValue = calculateValue(currentValue, action);
      if (validatedValue === value && currentValue !== value) {
        setCurrentValue(validatedValue);
      }
      if (onChange && !readOnly && !disabled) {
        onChange({
          event,
          value: validatedValue
        });
      }
    };

    const increment = (e) => {
      handleChange(e, ACTION.increment);
    };

    const decrement = (e) => {
      handleChange(e, ACTION.decrement);
    };

    const onInputChange = (e) => {
      if (Number.isNaN(e.target.value)) {
        return;
      }
      setCurrentValue(e.target.value);
    };

    const onInputFocus = (e) => {
      onFocus && onFocus(e);
      setInputFocused(true);
    };

    const onInputBlur = (e) => {
      onBlur && onBlur(e);
      setInputFocused(false);
      handleChange(e, ACTION.enter);
    };

    useEffect(() => {
      inputFocused && combinedInputRef.current.select();
    }, [inputFocused]);

    const handleDecrement = (e) => {
      // prevent the input text from being selected after double click this button
      !isFirefox.current && e.preventDefault();
      decrement(e);
      e.target.focus();
    };

    const handleIncrement = (e) => {
      !isFirefox.current && e.preventDefault();
      increment(e);
      e.target.focus();
    };

    /**
     * Handle key events for the component.
     * Supported keys include down key for decrement, up key for increment, and enter to apply value typed in.
     *
     * @param event The keyboard event that triggered this handler.
     */
    const handleKeyDown = (event) => {
      const { key, target } = event;
      switch (key) {
        case KEY_CODES.ARROW_UP:
          // If UP arrow key pressed, increment value.
          event.preventDefault();
          increment(event);
          break;
        case KEY_CODES.ARROW_DOWN:
          // If DOWN arrow key pressed, decrement value.
          event.preventDefault();
          decrement(event);
          break;
        case KEY_CODES.SPACE_KEY:
        case KEY_CODES.ENTER_KEY:
          // if ENTER key pressed, attempt to apply and update value.
          target.tagName === 'INPUT' && handleChange(event, ACTION.enter);
          break;
        case KEY_CODES.ARROW_LEFT:
          if (target.tagName === 'INPUT') {
            // if current target is input, focus on a button based on rtl
            const focusTarget = !RTL ? combinedDecBtnRef.current : combinedIncBtnRef.current;
            focusTarget.focus();
          } else {
            const action = target.attributes.getNamedItem('action').value;
            // check if the focus is on the most left IconButton. If not, focus on the input
            if ((!RTL && action !== ACTIONS.decrement) || (RTL && action !== ACTIONS.increment)) {
              combinedInputRef.current.focus();
            }
          }
          break;
        case KEY_CODES.ARROW_RIGHT:
          if (target.tagName === 'INPUT') {
            // if current target is input, focus on a button based on rtl
            const focusTarget = !RTL ? combinedIncBtnRef.current : combinedDecBtnRef.current;
            focusTarget.focus();
          } else {
            const action = target.attributes.getNamedItem('action').value;
            // check if the focus is on the most right IconButton. If not, focus on the input
            if ((!RTL && action !== ACTIONS.increment) || (RTL && action !== ACTIONS.decrement)) {
              combinedInputRef.current.focus();
            }
          }
          break;
        default:
          break;
      }
    };

    return (
      <styled.Spinner
        ref={ref}
        inputFocused={inputFocused}
        hasOutline={variant === 'outlined'}
        disabled={disabled}
        readOnly={readOnly}
        error={error}
        $theme={resolvedTheme}
        role="application"
        {...otherProps}
      >
        <styled.SpinnerContainer $theme={resolvedTheme} $rtl={RTL}>
          <IconButton
            disabled={disabled || readOnly}
            onClick={handleDecrement}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            ref={combinedDecBtnRef}
            {...otherDecBtnProps}
          >
            <RemoveIcon />
          </IconButton>
          <TextInput
            style={{ width: '3.5rem' }}
            disabled={disabled}
            placeholder={placeholder}
            inputProps={{
              style: {
                maxWidth: '3.5rem',
                height: '2rem',
                boxShadow: 'none',
                textAlign: 'center',
                padding: 0,
                backgroundColor: 'transparent'
              },
              ref: combinedInputRef,
              theme: resolvedTheme,
              onBlur: onInputBlur,
              onFocus: onInputFocus,
              onKeyDown: (e) => {
                handleKeyDown(e);
                onKeyDown && onKeyDown(e);
              },
              ...otherInputProps
            }}
            value={`${currentValue}`}
            onChange={onInputChange}
            readOnly={readOnly}
            {...otherTextInputProps}
          />
          <IconButton
            disabled={disabled || readOnly}
            onKeyDown={handleKeyDown}
            onClick={handleIncrement}
            tabIndex={0}
            ref={combinedIncBtnRef}
            {...otherIncBtnProps}
          >
            <AddIcon />
          </IconButton>
        </styled.SpinnerContainer>
      </styled.Spinner>
    );
  }));

Spinner.displayName = 'Spinner';

Spinner.defaultProps = {
  onChange: undefined,
  value: 1,
  variant: 'underlined',
  min: undefined,
  max: undefined,
  step: 1,
  values: undefined,
  error: false,
  inputProps: {},
  incrementButtonProps: {},
  decrementButtonProps: {},
  theme: {},
  onFocus: undefined,
  disabled: false,
  readOnly: false,
  placeholder: undefined,
};

export default Spinner;
